import styles from './DropshippingModalWindow.module.scss';
import { Modal } from '@components/Modal';
import Box from '@components/Box/Box.tsx';
import FormErrorMessage from '@components/FormErrorMessage';
import TextAreaContainer from '@containers/TextAreaContainer';
import TextInputContainer from '@containers/TextInputContainer';
import {
  addMatching,
  editMatching,
  getMatchingTariffs,
  getMatchingStatus,
  TMatchingTariff,
  TGetMatchingOrderStatusResponse,
} from '@api/apiClient/dropshipping';
import {
  checkBalanceReplenishment,
  checkBalanceReplenishmentResponse,
  getPaymentLink,
  getWallet,
  sendTopUpBalance,
  sendTopUpBalanceResponse,
  TGetPaymentLinkResponse,
} from '@api/apiClient/finance';
import { useProfile } from "@contexts/ProfileContext";
import { Button, ButtonType } from '@components/Button';
import { Column, Grid, Row } from '@components/Grid';
import { IDropshippingModalWindowProps, FormType } from '../../types';
import PinkWalletIcon from "@assets/icons/pink-wallet.svg";
import WalletIcon from "@assets/icons/wallet.svg"
import PaymentModalWindow from '@components/modals/PaymentModalWindow';
import { MessageModalContent } from '@components/modals/MessageModal';
import { Text, TextSize, TextType, TextWeight, TextColor } from '@components/Text';
import { formatSumFromObject } from "@utils/formatValues";
import { useState, useEffect, useRef } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { useMutation, useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';

const DropshippingModalWindow = (props: IDropshippingModalWindowProps) => {
  const {
    calledFrom,
    handlerRefreshData,
    onClose,
    linkedItems,
    linkedItemsComment,
    linkedItemsMatchingId,
    linkedItemsTrackNumbers
  } = props;

  const isInitialized = useRef(false);
  const isOneToMany = linkedItems.length === 1;
  const { t } = useTranslation();
  const { account, get } = useProfile();
  const timeoutCheckStatus = useRef<number | null>(null);
  const timeoutCheckBalance = useRef<number | null>(null);
  const timeoutCheckPayment = useRef<number | null>(null);
  const [paymentReturnError, setPaymentReturnError] = useState<string | undefined>(undefined);
  const [isOpenPaymentWindow, setIsOpenPaymentWindow] = useState(false);
  const [paymentInProcessed, setPaymentInProcessed] = useState<boolean>(false);
  const [orderInProcessed, setOrderInProcessed] = useState<boolean>(false);
  const [step, setStep] = useState<string>("form");
  const [tariff, setTariff] = useState<{ amount: number, currency: string } | undefined>();

  const tariffsQuery = useQuery({
    queryKey: ['getMatchingTariffs'],
    queryFn: () => getMatchingTariffs(),
    retry: 1,
  });

  useEffect(() => {
    if (tariffsQuery.data && account?.region) {
      const countryTariff: TMatchingTariff | undefined = tariffsQuery.data.tariffs
        .find((item: TMatchingTariff) => item.countryCode === account?.region);
      setTariff(countryTariff?.price);
    }
  }, [tariffsQuery.data, account?.region]);

  const paymentSum = (tariff?.amount ?? 0) * linkedItems.length;

  const walletQuery = useQuery({
    queryKey: ['getWallet'],
    queryFn: () => getWallet(),
    retry: 1,
  });
  const currency = account?.region === "TR" ? "USD" : "CNY";
  const lackOfFunds: number = Math.max(paymentSum - (walletQuery.data?.balance ?? 0), 0);

  const {
    data: dataSendTopUpBalance,
    error: errorSendTopUpBalance,
    isError: isErrorSendTopUpBalance,
    isLoading: isLoadingSendTopUpBalance,
    mutate: mutateSendTopUpBalance,
    // reset: resetSendTopUpBalance
  } = useMutation({
    mutationFn: sendTopUpBalance,
    onSuccess: (result: sendTopUpBalanceResponse) => {
      setIsOpenPaymentWindow(true);
      // setIsOpenReturnProcessingWindow(false);
      setPaymentTimeout();
      waitingBalanceReplenished(result.outTradeNo);
    }
  });

  const {
    error: errorGetPaymentLink,
    isError: isErrorGetPaymentLink,
    isLoading: isLoadingGetPaymentLink,
    mutate: mutateGetPaymentLink
  } = useMutation({
    mutationFn: getPaymentLink,
    onSuccess: (result: TGetPaymentLinkResponse) => {
      setPaymentInProcessed(true);
      setPaymentTimeout();
      waitingBalanceReplenished();
      window.open(result.paymentLink);
    }
  });

  const { mutate: mutateCheckBalance } = useMutation({
    mutationFn: getWallet,
    onSuccess: (result) => {
      if (result.balance >= paymentSum) {
        onSaveHandler()
      } else {
        waitingBalanceReplenished();
      }
    }
  });

  const { mutate: mutateCheckBalanceReplenishment } = useMutation({
    mutationFn: checkBalanceReplenishment,
  });

  const {
    error: errorAddMatching,
    isError: isErrorAddMatching,
    isLoading: isLoadingAddMatching,
    mutate: mutateAddMatching,
  } = useMutation({
    mutationFn: addMatching,
    onSuccess: (data) => {
      setOrderInProcessed(true);
      waitingOrderStatus(data?.matchingOrderId);
    }
  });

  const {
    error: errorEditMatching,
    isError: isErrorEditMatching,
    isLoading: isLoadingEditMatching,
    mutate: mutateEditMatching,
  } = useMutation({
    mutationFn: editMatching,
    onSuccess: () => {
      handlerRefreshData();
      onClose();
    }
  });

  const { handleSubmit, control, setValue, getValues } = useForm<FormType>({
    mode: 'onChange',
    defaultValues: {
      comment: '',
      trackNumbers: [{ externalMarketplaceTrackNumber: '', id: '' }]
    }
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'trackNumbers'
  });

  const onSaveHandler = () => {
    const trackNumbers = getValues("trackNumbers");
    mutateAddMatching({
      externalMarketplaceCodes: trackNumbers.map(item => item.externalMarketplaceTrackNumber),
      parcelExternalIds: linkedItems,
      message: (getValues("comment") ?? "").trim() || null,
    });
  };

  const submit = (form: FormType) => {
    if (calledFrom === 'inProcess') {
      mutateEditMatching({
        externalParcels: form.trackNumbers,
        matchingId: linkedItemsMatchingId!,
        message: (form.comment ?? "").trim() || null,
      });
    } else {
      if (lackOfFunds) {
        if (account!.region === 'TR') {
          setIsOpenPaymentWindow(true);
        } else {
          mutateSendTopUpBalance({ amount: lackOfFunds });
        }
      } else {
        onSaveHandler();
      }
    }
  };

  useEffect(() => {
    if (isInitialized.current) return;
    isInitialized.current = true;
    if (calledFrom === 'inProcess') {
      remove(0);
      setValue('comment', linkedItemsComment!);
      for (let i = 0; i < linkedItemsTrackNumbers!.length; i++) {
        const linkedItem = linkedItemsTrackNumbers![i];
        append({
          externalMarketplaceTrackNumber: linkedItem.externalMarketplaceTrackNumber,
          id: linkedItem.id
        });
      }
    }
  }, []);

  const waitingBalanceReplenished = (id?: string): void => {
    timeoutCheckBalance.current = window.setTimeout(() => {
      if (id) {
        mutateCheckBalanceReplenishment(id, {
          onSuccess: (data: checkBalanceReplenishmentResponse) => {
            if (data.isProcess) {
              if (!paymentInProcessed) setPaymentInProcessed(true);
              waitingBalanceReplenished(id);
            } else {
              if (paymentInProcessed) setPaymentInProcessed(false);
              if (data.isSuccess) {
                clearTimeout(timeoutCheckPayment.current as number);
                get();
                setIsOpenPaymentWindow(false);
                onSaveHandler();
              } else {
                if (data.errorCode) {
                  clearTimeout(timeoutCheckPayment.current as number);
                  setPaymentReturnError(data.errorCode);
                } else {
                  waitingBalanceReplenished(id);
                }
              }
            }
          }
        });
      } else {
        mutateCheckBalance();
      }
    }, 3000);
  };

  const { mutate: mutateCheckOrderStatus } = useMutation({
    mutationFn: getMatchingStatus,
  });

  const waitingOrderStatus = (orderId: string): void => {
    timeoutCheckStatus.current = window.setTimeout(() => {
      mutateCheckOrderStatus(orderId, {
        onSuccess: (data: TGetMatchingOrderStatusResponse) => {
          if (data.status === 'Payed') {
            get();
            setStep("success");
            setIsOpenPaymentWindow(false);
            setPaymentInProcessed(false);
            setOrderInProcessed(false);
            handlerRefreshData();
          } else if (data.status === 'PaymentError') {
            get();
            setStep("error");
            setIsOpenPaymentWindow(false);
            setPaymentInProcessed(false);
            setOrderInProcessed(false);
            handlerRefreshData();
          } else {
            waitingOrderStatus(orderId);
          }
        }
      });
    }, 3000);
  };

  const onClickPaymentLink = (amount: string) => {
    mutateGetPaymentLink(amount);
  };

  const setPaymentTimeout = (): void => {
    timeoutCheckPayment.current = window.setTimeout(() => {
      clearTimeout(timeoutCheckBalance.current as number);
      setPaymentInProcessed(false);
    }, 180000);
  };

  useEffect(() => {
    return () => {
      clearTimeout(timeoutCheckBalance.current as number);
      clearTimeout(timeoutCheckPayment.current as number);
    };
  }, [])

  if (step === "success") {
    return (
      <Modal
        isOpen
        onClickOverlay={onClose}
        onClose={onClose}
        width={560}
      >
        <MessageModalContent
          title={t("dropshipping.creationModal.successTitle")}
          description={t("dropshipping.creationModal.successDescription")}
          onClose={onClose}
          mode="success"
        />
      </Modal>
    );
  }

  if (step === "error") {
    return (
      <Modal
        isOpen
        onClickOverlay={onClose}
        onClose={onClose}
        width={560}
      >
        <MessageModalContent
          mode="warning"
          title={t("dropshipping.creationModal.insufficientFunds.title")}
          description={t("dropshipping.creationModal.insufficientFunds.description")}
          onClose={onClose}
        />
      </Modal>
    );
  }

  if (isOpenPaymentWindow) {
    return (
      <Modal
        isOpen
        onClickOverlay={onClose}
        onClose={onClose}
        width={650}
      >
        <PaymentModalWindow
          dataSendTopUpBalance={dataSendTopUpBalance!}
          errorGetPaymentLink={errorGetPaymentLink}
          handlerOnClickPaymentLink={onClickPaymentLink}
          isErrorGetPaymentLink={isErrorGetPaymentLink}
          isLoadingGetPaymentLink={isLoadingGetPaymentLink}
          isLoadingSendTopUpBalance={isLoadingSendTopUpBalance}
          mutateSendTopUpBalance={mutateSendTopUpBalance}
          paymentError={paymentReturnError}
          paymentInProcessed={paymentInProcessed}
          selectedSum={paymentSum}
          subTitle={t('dropshipping.creationModal.paymentSubtitle')}
        />
      </Modal>
    )
  }

  return (
    <Modal
      isOpen
      onClickOverlay={onClose}
      onClose={onClose}
      width={650}
    >
      <Grid>
        <Row>
          <Column phone={12}>
            <Box mb={5}>
              <Text type={TextType.H1}>
                {calledFrom === 'new' && t(`dropshipping.creationModal.${isOneToMany ? 'oneToMany' : 'manyToOne'}.title`)}
                {calledFrom === 'inProcess' && t('dropshipping.creationModal.edit.title')}
              </Text>
            </Box>
            <Box pb={4}>
              <Text size={TextSize.M}>
                {t(`dropshipping.creationModal.${isOneToMany ? 'oneToMany' : 'manyToOne'}.description`)}
              </Text>
            </Box>
          </Column>
        </Row>

        {calledFrom === 'new' && (
          <Box flex="column" mb={5} p={5} className={styles.greyBox}>
            <Box flex="row">
              <Box flex="column">
                <Text size={TextSize.M} color={TextColor.PRIMARY}>
                  {t("dropshipping.creationModal.cost")}
                </Text>
                <Box pt={1}>
                  <Text size={TextSize.L} color={TextColor.PRIMARY} weight={TextWeight.BOLD}>
                    {formatSumFromObject({
                      amount: paymentSum,
                      currency: currency,
                    })}
                  </Text>
                </Box>
              </Box>
              <Box flex="column" pl={12}>
                <Text size={TextSize.M} color={TextColor.PRIMARY}>
                  {t("dropshipping.creationModal.balance")}
                </Text>
                <Box flex="row" pt={1}>
                  <img
                    src={lackOfFunds ? PinkWalletIcon : WalletIcon}
                    alt="wallet"
                    style={{ marginRight: "5px", width: "15px" }}
                  />
                  <Text
                    size={TextSize.L}
                    color={lackOfFunds ? TextColor.WARM_PINK : TextColor.PRIMARY}
                    weight={TextWeight.BOLD}
                  >
                    {formatSumFromObject({
                      amount: walletQuery.data?.balance ?? 0,
                      currency: currency,
                    }) || "0"}
                  </Text>
                </Box>
              </Box>
              {lackOfFunds ? (
                <Box flex="column" pl={12}>
                  <Text size={TextSize.M} color={TextColor.PRIMARY}>
                    {t("dropshipping.creationModal.short")}
                  </Text>
                  <Box pt={1}>
                    <Text size={TextSize.L} color={TextColor.PRIMARY} weight={TextWeight.BOLD}>
                      {formatSumFromObject({
                        amount: lackOfFunds,
                        currency: currency,
                      }) || "0"}
                    </Text>
                  </Box>
                </Box>
              ) : (
                <Box flex="column" pl={12}>{" "}</Box>
              )}
            </Box>
          </Box>
        )}

        <div className={styles.info}>
          <div className={styles.infoPoint}>
            <Text
                type={TextType.H3}
                weight={TextWeight.BOLD}
            >
              {calledFrom === 'new' && t('dropshipping.creationModal.marketplaceNumber')}
              {calledFrom === 'inProcess' && t('dropshipping.creationModal.orderNumberOzon')}
            </Text>
            <div className={styles.linkedItems}>
              {(linkedItems || []).map((item: string) => (
                  <div
                    className={styles.linkedItemsElement}
                    key={item}
                  >
                    {item}
                  </div>
              ))}
            </div>
          </div>
          <div className={styles.infoPoint}>
            <Text
                type={TextType.H3}
                weight={TextWeight.BOLD}
            >
              {calledFrom === 'new' && t('dropshipping.creationModal.outerPackage')}
              {calledFrom === 'inProcess' && t('dropshipping.creationModal.otherMarketplaceNumber')}
            </Text>
            <div className={styles.trackNumbers}>
              {fields.map((field, index) => (
                  <div
                      key={field.id}
                      className={styles.trackNumbersWrapper}
                  >
                    <Box pb={4}>
                      <Text size={TextSize.M}>
                        {t('dropshipping.creationModal.trackNumberInputDescription')}
                      </Text>
                    </Box>

                    <TextInputContainer
                        key={field.id}
                        rules={{
                          required: t('defaultFormErrors.required'),
                          pattern: {
                            value: /^(?!UNI)/,
                            message: t('dropshipping.creationModal.trackNumberInputError')
                          }
                        }}
                        control={control}
                        name={`trackNumbers.${index}.externalMarketplaceTrackNumber`}
                        title={t('dropshipping.creationModal.trackNumberInput')}
                    />
                    {(isOneToMany || calledFrom === 'inProcess') && (
                        <button
                            className={styles.removeBtn}
                            onClick={() => {
                              if (fields.length > 1) {
                                remove(index);
                              } else {
                                setValue(`trackNumbers.${index}.externalMarketplaceTrackNumber`, '');
                              }
                            }}
                        />
                    )}
                  </div>
              ))}
              {isOneToMany && (
                  <span>
                    <Button
                        onClick={() => append({ externalMarketplaceTrackNumber: '', id: null })}
                        title={(<b>+ {t('dropshipping.creationModal.oneToMany.addTrackNumber')}</b>)}
                        type={ButtonType.LINK}
                    />
                  </span>
              )}
              <TextAreaContainer
                  name="comment"
                  control={control}
                  title={t('dropshipping.creationModal.commentInput')}
                  rows={3}
              />
            </div>
          </div>
        </div>
        <div className={styles.actions}>
          {calledFrom === 'new' ? (
            <Button
              onClick={onClose}
              pending={isLoadingAddMatching || isLoadingEditMatching || orderInProcessed}
              disabled={isLoadingAddMatching || isLoadingEditMatching || orderInProcessed}
              title={t(`dropshipping.creationModal.cancelBtn`)}
              type={ButtonType.SKELETON}
            />
          ) : null}
          <Button
            onClick={handleSubmit(submit)}
            pending={isLoadingAddMatching || isLoadingEditMatching || orderInProcessed || tariffsQuery.isLoading}
            disabled={isLoadingAddMatching || isLoadingEditMatching || orderInProcessed || tariffsQuery.isLoading}
            title={t(`dropshipping.creationModal.${calledFrom === 'new' ? 'payBtn' : 'saveLink'}`)}
          />
        </div>
        {isErrorAddMatching && <FormErrorMessage error={errorAddMatching}/>}
        {isErrorEditMatching && <FormErrorMessage error={errorEditMatching}/>}
        {isErrorSendTopUpBalance && (
          <FormErrorMessage error={errorSendTopUpBalance} />
        )}
        {isErrorSendTopUpBalance && (
          <FormErrorMessage error={errorSendTopUpBalance} />
        )}
      </Grid>
    </Modal>
  );
};

export default DropshippingModalWindow;
